
import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileCode, ChevronsRight, Terminal, Download } from 'lucide-react';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';

const TechnicalReference: React.FC = () => {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-6">
        <Badge variant="outline" className="px-2 py-1">
          <FileCode className="h-3 w-3 mr-1" /> Technical Documentation
        </Badge>
        <span className="text-muted-foreground text-sm">v1.0.3</span>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="architecture">Architecture</TabsTrigger>
          <TabsTrigger value="api">Full API</TabsTrigger>
          <TabsTrigger value="debug">Debug Tools</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="space-y-4 pt-4">
          <section>
            <h3 className="text-lg font-semibold mb-2">Widget Architecture</h3>
            <p className="text-muted-foreground">
              The Accesstopia widget is designed as a self-contained, shadow DOM encapsulated web component 
              that provides accessibility features without interfering with your site's existing styles or 
              functionality.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="p-4 rounded-md border bg-background/50">
                <h4 className="font-medium mb-2">Core Components</h4>
                <ul className="list-disc pl-5 text-sm space-y-1 text-muted-foreground">
                  <li>Accessibility Panel</li>
                  <li>Voice Assistant</li>
                  <li>Screen Reader Support</li>
                  <li>Keyboard Navigation</li>
                  <li>Color Adjustments</li>
                </ul>
              </div>
              <div className="p-4 rounded-md border bg-background/50">
                <h4 className="font-medium mb-2">Technical Features</h4>
                <ul className="list-disc pl-5 text-sm space-y-1 text-muted-foreground">
                  <li>Shadow DOM Encapsulation</li>
                  <li>Cross-browser Compatibility</li>
                  <li>Responsive Design</li>
                  <li>Custom Event System</li>
                  <li>State Management</li>
                </ul>
              </div>
              <div className="p-4 rounded-md border bg-background/50">
                <h4 className="font-medium mb-2">Integration Points</h4>
                <ul className="list-disc pl-5 text-sm space-y-1 text-muted-foreground">
                  <li>DOM Mutation Observers</li>
                  <li>Event Listeners</li>
                  <li>Global API Methods</li>
                  <li>Custom Command Handlers</li>
                  <li>State Change Callbacks</li>
                </ul>
              </div>
            </div>
          </section>
          
          <section className="mt-8">
            <h3 className="text-lg font-semibold mb-2">Versioning Policy</h3>
            <p className="text-muted-foreground">
              The Accesstopia widget follows semantic versioning (MAJOR.MINOR.PATCH):
            </p>
            <ul className="list-disc pl-5 text-sm mt-2 space-y-1">
              <li><strong>MAJOR</strong> version changes include breaking API changes</li>
              <li><strong>MINOR</strong> version changes add functionality in a backward-compatible manner</li>
              <li><strong>PATCH</strong> version changes include backward-compatible bug fixes</li>
            </ul>
          </section>
        </TabsContent>
        
        <TabsContent value="architecture" className="space-y-4 pt-4">
          <section>
            <h3 className="text-lg font-semibold mb-2">Technical Architecture</h3>
            <div className="p-4 bg-muted/50 rounded-md">
              <pre className="text-xs overflow-x-auto whitespace-pre">
{`Accesstopia Widget Architecture
┌──────────────────────────────────────────────────────┐
│ Shadow DOM Root                                      │
│  ┌────────────────────────────────────────────────┐  │
│  │ Widget Container                               │  │
│  │  ┌────────────────┐       ┌─────────────────┐  │  │
│  │  │                │       │                 │  │  │
│  │  │  Toggle Button │◄──────┤  Event Manager  │  │  │
│  │  │                │       │                 │  │  │
│  │  └────────────────┘       └─────────────────┘  │  │
│  │          │                        ▲            │  │
│  │          ▼                        │            │  │
│  │  ┌────────────────┐       ┌─────────────────┐  │  │
│  │  │                │       │                 │  │  │
│  │  │ Settings Panel │◄─────►│  State Manager  │  │  │
│  │  │                │       │                 │  │  │
│  │  └────────────────┘       └─────────────────┘  │  │
│  │          │                        ▲            │  │
│  │          ▼                        │            │  │
│  │  ┌────────────────┐       ┌─────────────────┐  │  │
│  │  │                │       │                 │  │  │
│  │  │Voice Assistant │◄─────►│  Command Parser │  │  │
│  │  │                │       │                 │  │  │
│  │  └────────────────┘       └─────────────────┘  │  │
│  └────────────────────────────────────────────────┘  │
└──────────────────────────────────────────────────────┘
            │                        ▲
            │                        │
            ▼                        │
┌──────────────────────────────────────────────────────┐
│                                                      │
│                  Host Website                        │
│                                                      │
└──────────────────────────────────────────────────────┘`}
              </pre>
            </div>

            <div className="mt-6 space-y-4">
              <h4 className="font-medium">Key Components</h4>
              
              <div className="space-y-2">
                <h5 className="text-sm font-medium">Toggle Button</h5>
                <p className="text-sm text-muted-foreground">
                  The main entry point for users to access accessibility features. Positioned according to 
                  the <code>buttonPosition</code> configuration.
                </p>
              </div>
              
              <div className="space-y-2">
                <h5 className="text-sm font-medium">Settings Panel</h5>
                <p className="text-sm text-muted-foreground">
                  Contains UI controls for all accessibility features. Dynamically renders based on browser 
                  capabilities and configuration.
                </p>
              </div>
              
              <div className="space-y-2">
                <h5 className="text-sm font-medium">Voice Assistant</h5>
                <p className="text-sm text-muted-foreground">
                  Provides speech recognition and response capabilities. Only activated when explicitly 
                  enabled in configuration and supported by the browser.
                </p>
              </div>
              
              <div className="space-y-2">
                <h5 className="text-sm font-medium">State Manager</h5>
                <p className="text-sm text-muted-foreground">
                  Central store for all widget settings. Persists user preferences in localStorage and 
                  broadcasts state changes via custom events.
                </p>
              </div>
              
              <div className="space-y-2">
                <h5 className="text-sm font-medium">Command Parser</h5>
                <p className="text-sm text-muted-foreground">
                  Interprets voice commands and routes them to appropriate handlers. Supports custom command 
                  registration via the API.
                </p>
              </div>
            </div>
          </section>
          
          <section className="mt-6">
            <h3 className="text-lg font-semibold mb-2">Integration Points</h3>
            <p className="text-muted-foreground mb-4">
              The widget exposes several integration points for developers:
            </p>
            
            <div className="bg-muted/50 p-4 rounded-md">
              <h4 className="font-medium mb-2">Global API</h4>
              <p className="text-sm text-muted-foreground">
                The global <code>window.Accesstopia</code> object provides programmatic control of the widget.
              </p>
              <SyntaxHighlighter
                language="javascript"
                style={tomorrow}
                customStyle={{
                  fontSize: '0.85rem',
                  borderRadius: '0.5rem',
                  padding: '1rem',
                  marginTop: '0.5rem'
                }}
              >
{`// Example of programmatic control
window.Accesstopia.show();  // Show the panel
window.Accesstopia.setState({ darkMode: true });  // Enable dark mode
window.Accesstopia.registerCommand('openMenu', () => {
  document.getElementById('main-menu').click();
});`}
              </SyntaxHighlighter>
            </div>
            
            <div className="bg-muted/50 p-4 rounded-md mt-4">
              <h4 className="font-medium mb-2">DOM Modifications</h4>
              <p className="text-sm text-muted-foreground">
                The widget automatically applies accessibility enhancements to the host page:
              </p>
              <ul className="list-disc pl-5 text-sm mt-2">
                <li>Adds ARIA attributes to improve screen reader compatibility</li>
                <li>Injects dynamic CSS for text scaling and contrast improvements</li>
                <li>Enhances keyboard navigation with focus indicators</li>
                <li>Creates invisible landmarks for speech navigation</li>
              </ul>
            </div>
          </section>
        </TabsContent>
        
        <TabsContent value="api" className="space-y-4 pt-4">
          <section>
            <h3 className="text-lg font-semibold mb-2">Complete API Reference</h3>
            
            <div className="bg-muted/50 p-4 rounded-md">
              <h4 className="font-medium mb-2">Configuration Options</h4>
              <SyntaxHighlighter
                language="typescript"
                style={tomorrow}
                customStyle={{
                  fontSize: '0.85rem',
                  borderRadius: '0.5rem',
                  padding: '1rem',
                }}
              >
{`interface AccesstopiaConfig {
  // Display options
  buttonPosition?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
  voiceAssistantPosition?: 'bottom-center' | 'bottom-left' | 'bottom-right';
  primaryColor?: string;  // HEX color code
  borderRadius?: string;  // CSS border-radius value
  fontFamily?: string;    // CSS font-family value
  zIndex?: number;        // Base z-index for all widget elements
  
  // Feature flags
  enableVoiceAssistant?: boolean;
  enableKeyboardShortcuts?: boolean;
  enableAutoAnnounce?: boolean;  // Announce dynamic content changes
  enablePageStructure?: boolean; // Add ARIA landmarks for navigation
  
  // Default settings
  defaultDarkMode?: boolean;
  defaultFontSize?: number;      // 100 = normal, range: 80-200
  defaultLineHeight?: number;    // 1 = normal, range: 1-2
  defaultHighContrast?: boolean;
  defaultReduceMotion?: boolean;
  
  // Voice configuration
  language?: string;             // e.g., 'en-US', 'es-ES', etc.
  voiceRate?: number;            // Speech rate, 1 = normal
  voicePitch?: number;           // Speech pitch, 1 = normal
  
  // Navigation and routes
  routes?: Record<string, string>; // Named routes for voice navigation
  
  // Custom command mapping
  commands?: Record<string, Function | string>;
  customCommandPlugins?: Array<{
    triggers: string | string[];
    action: Function;
    description?: string;
    condition?: Function;
  }>;
  
  // Custom responses
  customResponses?: {
    navigate?: string;
    searchPrefix?: string;
    help?: string;
    notFound?: string;
    [key: string]: string;
  };
  
  // Callback hooks
  onInit?: (instance: AccesstopiaInstance) => void;
  onVoiceCommand?: (command: string, wasHandled: boolean) => boolean;
  onStateChange?: (newState: AccesstopiaState, oldState: AccesstopiaState) => void;
  onError?: (error: AccesstopiaError) => void;
  
  // Advanced options
  debug?: boolean;
  logLevel?: 'error' | 'warn' | 'info' | 'debug';
  scopeSelector?: string;   // Limit widget's effect to specific elements
  excludeSelector?: string; // Exclude elements from widget modifications
  
  // Storage
  storageKey?: string;     // LocalStorage key for saving preferences
  storageExpiry?: number;  // Days until stored preferences expire
}`}
              </SyntaxHighlighter>
            </div>
            
            <div className="bg-muted/50 p-4 rounded-md mt-4">
              <h4 className="font-medium mb-2">Global API Methods</h4>
              <SyntaxHighlighter
                language="typescript"
                style={tomorrow}
                customStyle={{
                  fontSize: '0.85rem',
                  borderRadius: '0.5rem',
                  padding: '1rem',
                }}
              >
{`interface AccesstopiaInstance {
  // Core methods
  show(): void;                     // Show the accessibility panel
  hide(): void;                     // Hide the accessibility panel
  toggle(): void;                   // Toggle panel visibility
  
  // State management
  getState(): AccesstopiaState;     // Get current settings
  setState(state: Partial<AccesstopiaState>): void;  // Update settings
  resetState(): void;               // Reset to defaults
  
  // Voice assistant
  startVoiceAssistant(): Promise<boolean>;  // Activate voice assistant
  stopVoiceAssistant(): void;               // Deactivate voice assistant
  speak(text: string, options?: SpeechOptions): void;  // Text-to-speech
  
  // Commands and navigation
  registerCommand(
    trigger: string | string[],
    handler: Function, 
    options?: CommandOptions
  ): string;  // Returns command ID
  
  unregisterCommand(commandId: string): boolean;
  
  navigateTo(route: string): boolean;  // Navigate to named route
  
  // Advanced features
  scan(): ScanResults;  // Scan page for accessibility issues
  
  applyFix(
    issueId: string, 
    options?: FixOptions
  ): Promise<FixResult>;  // Apply automatic fix
  
  highlight(selector: string): void;  // Highlight elements
  
  // Events
  on(event: AccesstopiaEventType, callback: Function): void;
  off(event: AccesstopiaEventType, callback: Function): void;
  
  // Utilities
  getVersion(): string;  // Get widget version
  destroy(): void;       // Remove widget completely
}

// Types
type AccesstopiaState = {
  darkMode: boolean;
  fontSize: number;
  lineHeight: number;
  highContrast: boolean;
  reduceMotion: boolean;
  highlightLinks: boolean;
  highlightHeadings: boolean;
  highlightFocus: boolean;
  imageAlt: boolean;
  forcedColors: boolean;
  alignCenter: boolean;
  readableFont: boolean;
  largeCursor: boolean;
  [key: string]: any;  // For custom settings
};

type AccesstopiaEventType = 
  | 'init'
  | 'stateChange'
  | 'voiceCommand'
  | 'navigation'
  | 'panelToggle'
  | 'error';

type ScanResults = {
  issues: Array<{
    id: string;
    type: 'error' | 'warning' | 'info';
    element: HTMLElement | null;
    description: string;
    impact: 'critical' | 'serious' | 'moderate' | 'minor';
    fixAvailable: boolean;
  }>;
  summary: {
    total: number;
    critical: number;
    serious: number;
    moderate: number;
    minor: number;
  };
};

type SpeechOptions = {
  rate?: number;
  pitch?: number;
  volume?: number;
  voice?: string;
  priority?: 'high' | 'normal' | 'low';
};

type CommandOptions = {
  description?: string;
  condition?: (command: string) => boolean;
  aliases?: string[];
};

type FixOptions = {
  applyAll?: boolean;  // Apply to all similar issues
  interactive?: boolean;  // Prompt user for input
};

type FixResult = {
  fixed: boolean;
  affectedElements: number;
  errorMessage?: string;
};

type AccesstopiaError = {
  code: string;
  message: string;
  details?: any;
};`}
              </SyntaxHighlighter>
            </div>
            
            <div className="bg-muted/50 p-4 rounded-md mt-4">
              <h4 className="font-medium mb-2">Events</h4>
              <SyntaxHighlighter
                language="javascript"
                style={tomorrow}
                customStyle={{
                  fontSize: '0.85rem',
                  borderRadius: '0.5rem',
                  padding: '1rem',
                }}
              >
{`// List of all available events
const AccesstopiaEvents = {
  // Lifecycle events
  INITIALIZED: 'accesstopia:initialized', // Widget fully loaded
  DESTROYED: 'accesstopia:destroyed',     // Widget removed
  
  // State events
  STATE_CHANGED: 'accesstopia:stateChanged',  // Settings updated
  
  // UI events
  PANEL_OPENED: 'accesstopia:panelOpened',    // Panel shown
  PANEL_CLOSED: 'accesstopia:panelClosed',    // Panel hidden
  
  // Voice events
  VOICE_STARTED: 'accesstopia:voiceStarted',  // Voice recognition started
  VOICE_STOPPED: 'accesstopia:voiceStopped',  // Voice recognition stopped
  VOICE_COMMAND: 'accesstopia:voiceCommand',  // Command detected
  
  // Action events
  NAVIGATION: 'accesstopia:navigation',       // Page navigation requested
  SCAN_COMPLETE: 'accesstopia:scanComplete',  // Accessibility scan finished
  FIX_APPLIED: 'accesstopia:fixApplied',      // Fix applied to issue
  
  // Error events
  ERROR: 'accesstopia:error'                  // Error occurred
};

// Example event listener
document.addEventListener('accesstopia:stateChanged', function(e) {
  console.log('Settings changed:', e.detail);
});`}
              </SyntaxHighlighter>
            </div>
          </section>
        </TabsContent>
        
        <TabsContent value="debug" className="space-y-4 pt-4">
          <section>
            <h3 className="text-lg font-semibold mb-2">Debugging Tools</h3>
            <p className="text-muted-foreground mb-4">
              Accesstopia provides several tools to help diagnose integration issues:
            </p>
            
            <div className="space-y-4">
              <div className="bg-muted/50 p-4 rounded-md">
                <h4 className="font-medium mb-2">Debug Mode</h4>
                <p className="text-sm text-muted-foreground">
                  Enable detailed logging to help troubleshoot integration issues:
                </p>
                <SyntaxHighlighter
                  language="javascript"
                  style={tomorrow}
                  customStyle={{
                    fontSize: '0.85rem',
                    borderRadius: '0.5rem',
                    padding: '1rem',
                    marginTop: '0.5rem'
                  }}
                >
{`// Enable debug mode
window.ACCESSTOPIA_CONFIG = {
  debug: true,
  logLevel: 'debug'  // Options: 'error', 'warn', 'info', 'debug'
};

// Access debug tools programmatically
window.Accesstopia.debug.getInfo();  // Returns system info
window.Accesstopia.debug.check();    // Run diagnostic tests
window.Accesstopia.debug.logState(); // Log current state`}
                </SyntaxHighlighter>
              </div>
              
              <div className="bg-muted/50 p-4 rounded-md">
                <h4 className="font-medium mb-2">Console Commands</h4>
                <p className="text-sm text-muted-foreground">
                  Execute these commands in your browser console for troubleshooting:
                </p>
                <SyntaxHighlighter
                  language="javascript"
                  style={tomorrow}
                  customStyle={{
                    fontSize: '0.85rem',
                    borderRadius: '0.5rem',
                    padding: '1rem',
                    marginTop: '0.5rem'
                  }}
                >
{`// Check widget status
console.log(window.Accesstopia);

// Test widget functionality
window.Accesstopia.test();

// Check browser compatibility
window.Accesstopia.checkCompatibility();

// View all registered commands
console.table(window.Accesstopia.listCommands());

// View current accessibility state
console.log(window.Accesstopia.getState());

// Check for common integration issues
window.Accesstopia.diagnose();`}
                </SyntaxHighlighter>
              </div>
            </div>
          </section>
          
          <section className="mt-6">
            <h3 className="text-lg font-semibold mb-2">Testing & Validation</h3>
            <div className="bg-muted/50 p-4 rounded-md">
              <h4 className="font-medium mb-2">Validation Tool</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Use our validation endpoint to verify your installation:
              </p>
              <SyntaxHighlighter
                language="javascript"
                style={tomorrow}
                customStyle={{
                  fontSize: '0.85rem',
                  borderRadius: '0.5rem',
                  padding: '1rem',
                }}
              >
{`// Validate your installation
fetch('https://lmniterbzhfihvxsumbl.supabase.co/functions/v1/widget?key=YOUR_KEY&verify=true')
  .then(response => response.json())
  .then(data => {
    if (data.valid) {
      console.log('Installation valid for domain:', data.domain);
    } else {
      console.error('Invalid installation:', data.message);
    }
  })
  .catch(error => console.error('Verification error:', error));`}
              </SyntaxHighlighter>

              <div className="flex items-center mt-4 gap-2">
                <Button variant="outline" size="sm" className="flex items-center gap-1">
                  <Terminal className="h-3 w-3" /> Run Validation Test
                </Button>
                <Button variant="secondary" size="sm" className="flex items-center gap-1">
                  <Download className="h-3 w-3" /> Download Debug Report
                </Button>
              </div>
            </div>
            
            <div className="bg-muted/50 p-4 rounded-md mt-4">
              <h4 className="font-medium mb-2">Browser Support</h4>
              <p className="text-sm text-muted-foreground">
                Core accessibility features are supported across all modern browsers. Advanced features like voice
                commands have varying support:
              </p>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <div>
                  <h5 className="text-xs font-medium mb-1">Full Support</h5>
                  <ul className="list-disc pl-5 text-xs space-y-1 text-muted-foreground">
                    <li>Chrome 83+</li>
                    <li>Edge 83+</li>
                    <li>Safari 13.1+</li>
                    <li>Opera 69+</li>
                  </ul>
                </div>
                <div>
                  <h5 className="text-xs font-medium mb-1">Partial Support*</h5>
                  <ul className="list-disc pl-5 text-xs space-y-1 text-muted-foreground">
                    <li>Firefox (no voice assistant)</li>
                    <li>Chrome for Android</li>
                    <li>Safari iOS 14.5+</li>
                  </ul>
                  <p className="text-xs mt-1">* Voice features may be limited</p>
                </div>
              </div>
            </div>
          </section>
        </TabsContent>
      </Tabs>
      
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" className="flex items-center gap-1">
          <ChevronsRight className="h-3 w-3" /> View Sample Implementation
        </Button>
        <Button size="sm" className="flex items-center gap-1">
          <FileCode className="h-3 w-3" /> Full Documentation
        </Button>
      </div>
    </div>
  );
};

export default TechnicalReference;
